import React from "react";
import styled from "styled-components";
import PropTypes from "prop-types";
import { PictureAsPdfRounded } from "@mui/icons-material";
import { IconButton, Tooltip } from "@mui/material";
import { WarningRounded } from "@mui/icons-material";
import { useSelector } from "react-redux";

import { H3 } from "commons";
import { format, FORMAT } from "commons/src/NuDateUtils";
import { isFailure } from "commons/src/helpers/RequestHelpers";
import { getDocumentIdString } from "commons/src/helpers/documentHelpers";

import { markAsRead } from "../../components/DataOperations";

export function Document({ id, name, read, link, account, sentDate }) {
    const adviserId = useSelector(state => state.user.adviserId);
    const markAsReadStatus = useSelector(
        state => state.requests?.[getDocumentIdString(id)]?.status
    );
    const isAdviser = Boolean(adviserId);

    const markAsReadAndDownload = event => {
        if (!read && !isAdviser) {
            markAsRead(id, sentDate);
        }
        window.open(link, "", "noopener, noreferrer");
        event.stopPropagation();
    };

    return (
        <DocumentBlock data-testid="document-block">
            <Content>
                <ReadIndicator read={read} />
                <Column>
                    <DocumentBlockHeaderContainer>
                        <Button onClick={markAsReadAndDownload} data-testid="document-name">
                            <H3>{name}</H3>
                        </Button>
                        {isFailure(markAsReadStatus) ? (
                            <Tooltip
                                placement="top"
                                title="There was a problem marking this document as read. It might show as unread on future logins."
                            >
                                <WarningIcon fontSize="small" />
                            </Tooltip>
                        ) : null}
                    </DocumentBlockHeaderContainer>
                    {account && <Field data-testid="account-field">{account}</Field>}
                    <Field>{format(sentDate, FORMAT.SLASH_SEPARATED)}</Field>
                </Column>
            </Content>
            <IconButton
                aria-label="download PDF"
                onClick={markAsReadAndDownload}
                data-testid="pdf-download-button"
                size="large"
            >
                <PictureAsPdfRounded color="secondary" />
            </IconButton>
        </DocumentBlock>
    );
}

Document.propTypes = {
    id: PropTypes.number,
    name: PropTypes.string,
    read: PropTypes.bool,
    link: PropTypes.string,
    account: PropTypes.string,
    sentDate: PropTypes.string
};

const Content = styled.div`
    display: flex;
`;

const Button = styled.button`
    cursor: pointer;
    background: none;
    border: none;
    padding: 0;
`;

const Column = styled.div`
    display: flex;
    flex-direction: column;

    p:last-child {
        margin: 0;
    }
`;

export const Field = styled.p`
    display: block;
    text-align: left;
    color: ${props => props.theme.secondary};
`;

export const DownloadLink = styled.a`
    color: ${props => props.theme.secondary};
    font-size: 2.5em !important;
    cursor: pointer;
    margin-left: auto;
`;

const ReadIndicator = styled.div`
    width: 1em;
    min-width: 1em;
    height: 1em;
    background: ${props => props.theme.primary};
    border-radius: 50%;
    margin: 1px 0.5em 0 0;
    align-self: flex-start;
    ${props =>
        props.read ? `background: transparent; border: 1px solid ${props.theme.primary};` : ""}
`;

export const DocumentBlock = styled.div`
    padding: 1em 1em 1em 0.5em;
    background: ${props => props.theme.tertiary};
    margin: 1em 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

const DocumentBlockHeaderContainer = styled.div`
    display: flex;
    justify-content: flex-start;
`;

const WarningIcon = styled(WarningRounded)`
    color: ${props => props.theme.palette.warning.main};
    margin-left: 0.5rem;
`;
